import React, { useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import i18n from 'i18n-js';
import LinearGradient from 'react-native-linear-gradient';

import ColorWrapper from '@styles/colors';
import { isPhone as isPhoneDefault, isWeb, navigationRef } from '@helpers';

import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import FeatureFlags from '@components/featureFlags';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';
import Svg from '@styles/icons/svg';
import { LEVEL_ITEM_CONFIG } from './constants';

const Level = (props) => {
  const {
    selectedLevel,
    setSelectedLevel,
    tabs,
    C,
    checkWithPerspectiveBadge,
    onPerspectiveBadgePress,
    big = false,
    scale = initialScale,
    font = initialFont,
    levelSelectorBg = 'Button/Light/Tertiary',
    itemBG = 'Button/Light/Only Text',
    preselectedItemBG = 'Selected/Light/Slider Main Unselected',
    selectedItemBG = 'Selected/Light/Primary',
    preSelectedColor = 'Selected/Light/Slider Main Unselected Text',
    color = 'Label/Light/Unselected',
    selectedColor = 'Button/Light/Primary Text',
    blur,
    itemFont,
    valueFont = 'Subhead 1',
    itemStyle,
    alignLeft,
    isPopupMode,
    isPhone = isPhoneDefault,
  } = props;

  const route = navigationRef?.current?.getCurrentRoute();

  const light = '242, 243, 247';
  const dark = '0, 0, 0';

  const scrollView = useRef(null);
  const [x, setX] = useState(0);

  const refsElements = {};

  const base = C.scheme === 'light' ? `${light},` : `${dark},`;

  const length = tabs.length;
  const oneItemLength = 100 / length;

  const levelItemPaddingHorizontal = LEVEL_ITEM_CONFIG.getPaddingVertical({ big });

  return (
    <View
      style={[
        isPhone
          ? { justifyContent: 'center' }
          : {
              backgroundColor: C(levelSelectorBg),
              height: scale(big ? 58 : 48),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              padding: scale(4),
              width: '100%',
            },
      ]}
    >
      {blur && (
        <LinearGradient
          colors={[`rgba(${base} 0)`, `rgba(${base} 0.9)`, `rgba(${base} 1)`]}
          locations={[0, 0.87, 1]}
          style={{
            position: 'absolute',
            width: scale(7),
            left: scale(0),
            zIndex: 100,
            height: scale(33),
          }}
          start={{ x: 1, y: 0 }}
          end={{ x: 0, y: 0 }}
        />
      )}
      {blur && (
        <LinearGradient
          colors={[`rgba(${base} 0)`, `rgba(${base} 0.9)`, `rgba(${base} 1)`]}
          locations={[0, 0.87, 1]}
          style={{
            position: 'absolute',
            width: scale(7),
            right: scale(0),
            zIndex: 100,
            height: scale(33),
          }}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
        />
      )}
      <ScrollView
        scrollEnabled={blur}
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={
          isPhone
            ? {
                justifyContent: alignLeft ? 'flex-start' : 'center',
                minWidth: '100%',
                paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
                alignItems: 'center',
              }
            : {
                width: '100%',
              }
        }
        scrollEventThrottle={16}
        onScroll={(e) => {
          setX(e.nativeEvent.contentOffset.x);
        }}
        ref={scrollView}
      >
        {tabs.map((tab, index) => {
          const { buttonType, buttonText, key, name, value, empty, itemSliderBG, onPress, plural, tourId, testID: tabTestID, perspectiveBadgeTestID } = tab;

          const preselected = tab.preselected;
          const selected = selectedLevel ? selectedLevel === key : route?.name?.includes(key);

          const withPerspectiveBadge = checkWithPerspectiveBadge ? checkWithPerspectiveBadge({ selected, preselected, plural }) : false;

          const isNextSelected = selectedLevel ? tabs?.[index + 1]?.key === selectedLevel : route?.name?.includes(tabs?.[index + 1]?.key);
          const isNextPreselected = tabs?.[index + 1]?.preselected;
          const textColor = selected ? selectedColor : preselected ? preSelectedColor : color;
          const isLast = index === tabs.length - 1;
          return (
            <FeatureFlags {...{ name: tab.flag, C }} key={`${tab.name}-${index}-item`}>
              <View
                {...(isWeb && tourId ? { dataSet: { tourId } } : {})}
                style={[
                  {
                    userSelect: 'none',
                  },
                  !isPhone && {
                    width: `${oneItemLength}%`,
                    height: '100%',
                  },
                ]}
                key={key}
                ref={refsElements[key]}
              >
                <TouchableElement
                  testID={tabTestID}
                  onPress={() => {
                    if (empty) {
                      return null;
                    }

                    setSelectedLevel?.(key);

                    refsElements[key]?.current?.measure((fx, fy, width, height, px, py) => {
                      if (px < scale(30)) {
                        scrollView?.current?.scrollTo({ animated: true, x: x - (Math.abs(px) + scale(30)) });
                        return;
                      }
                      scrollView?.current?.measure((fx, fy, scrollWidth) => {
                        if (px + width + scale(30) > scrollWidth) {
                          const offset = px + width - scrollWidth + x;
                          scrollView?.current?.scrollTo({ animated: true, x: offset + scale(30) });
                        }
                      });
                    });

                    if (onPress) {
                      return onPress();
                    }
                  }}
                >
                  {({ boxStyle, pressHoverColor }) => (
                    <View
                      style={[
                        boxStyle,
                        {
                          paddingHorizontal: scale(LEVEL_ITEM_CONFIG.paddingHorizontal),
                          paddingVertical: scale(levelItemPaddingHorizontal),
                          borderRadius: scale(SCREEN_BORDER_RADIUS),
                          backgroundColor: selected
                            ? C(itemSliderBG || selectedItemBG)
                            : preselected
                            ? C(preselectedItemBG)
                            : C(pressHoverColor(itemBG)),
                          userSelect: 'none',
                        },
                        buttonType && { justifyContent: 'center', alignItems: 'center' },
                        itemStyle
                          ? itemStyle
                          : isPhone
                          ? { marginLeft: scale(10), marginRight: scale(10) }
                          : {
                              width: `100%`,
                              height: '100%',
                            },
                      ]}
                    >
                      {buttonType ? (
                        <Text style={[font['Button 1'], { color: C(selected ? 'Label/Dark/Primary' : 'Link/Light/Primary') }]}>
                          {i18n.t(buttonText)}
                        </Text>
                      ) : (
                        <>
                          <Text
                            style={[
                              font[itemFont || (isPhone ? 'Subhead 2' : big ? 'Headline' : 'Button 2')],
                              {
                                lineHeight: scale(isWeb ? 12 : 14),
                                textAlign: 'center',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                color: C(textColor),
                              },
                              !isPopupMode && !isPhone && { flex: 1 },
                            ]}
                          >
                            {empty ? '' : i18n.t(name)}
                          </Text>
                          {!isPhone && (
                            <Text
                              style={[
                                font[valueFont],
                                {
                                  ...(isWeb ? { lineHeight: scale(22) } : {}),
                                  color: C(textColor),
                                  flex: 1,
                                  width: '100%',
                                  textAlign: 'center',
                                },
                              ]}
                              numberOfLines={1}
                              ellipsizeMode="middle"
                            >
                              {empty ? '' : value}
                            </Text>
                          )}
                        </>
                      )}
                      {withPerspectiveBadge && (
                        <TouchableElement
                          testID={perspectiveBadgeTestID}
                          onPress={() => {
                            onPerspectiveBadgePress?.({ perspectiveToGo: tab?.perspectiveToGo });
                          }}
                        >
                          {({ hovered }) => (
                            <View
                              style={{
                                borderRadius: scale(20),
                                position: 'absolute',
                                top: scale(4),
                                right: scale(6),
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: scale(20),
                                height: scale(20),
                                backgroundColor: hovered
                                  ? C('Background/Light/Button background on image black text')
                                  : C('Background/Light/Button background on image'),
                              }}
                            >
                              <Svg icon="arrowLight" width={scale(20)} height={scale(20)} color={C('Label/Light/AlwaysLight Primary')} />
                            </View>
                          )}
                        </TouchableElement>
                      )}
                    </View>
                  )}
                </TouchableElement>
                {!(isLast || selected || isNextSelected || preselected || isNextPreselected || isPhone) && (
                  <View
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: scale(levelItemPaddingHorizontal),
                      bottom: scale(levelItemPaddingHorizontal),
                      width: StyleSheet.hairlineWidth,
                      backgroundColor: C('Hairline/Light/Secondary'),
                    }}
                  />
                )}
              </View>
            </FeatureFlags>
          );
        })}
      </ScrollView>
    </View>
  );
};

export default ColorWrapper(Level);
